import React, { Component } from 'react';
import { connect } from 'react-redux';
import get from 'lodash/get';
import { notification, Form, Input } from 'antd';
import { pending, rejected } from 'redux-saga-thunk';
import { compose } from 'redux';
import Settings from '../components/pages/Settings/index';
import {
  resourceUpdateRequest,
  resourceListReadRequest,
  resourceDeleteRequest,
} from '../store/resource/actions';
import { fromEntities, fromResource } from '../store/selectors';

const FormItem = Form.Item;
const EditableContext = React.createContext();

const EditableRow = ({ form, index, ...props }) => (
  <EditableContext.Provider value={form}>
    <tr {...props} />
  </EditableContext.Provider>
);

const EditableFormRow = Form.create()(EditableRow);

class EditableCell extends React.Component {
  state = {
    editing: false,
  };

  componentDidMount() {
    if (this.props.editable) {
      document.addEventListener('click', this.handleClickOutside, true);
    }
  }

  componentWillUnmount() {
    if (this.props.editable) {
      document.removeEventListener('click', this.handleClickOutside, true);
    }
  }

  toggleEdit = () => {
    const editing = !this.state.editing;
    this.setState({ editing }, () => {
      if (editing) {
        this.input.focus();
      }
    });
  };

  handleClickOutside = e => {
    const { editing } = this.state;
    if (editing && this.cell !== e.target && !this.cell.contains(e.target)) {
      this.save();
    }
  };

  save = () => {
    const { record, handleSave } = this.props;
    this.form.validateFields((error, values) => {
      if (error) {
        return;
      }
      this.toggleEdit();
      handleSave({ ...record, ...values })
        .then(() => {
          notification.success({ message: 'Success', description: 'Setting saved.', duration: 4 });
        })
        .catch(e => {
          notification.error({
            message: 'Error',
            description: typeof e === 'string' ? e : 'Could not save setting.',
            duration: 0,
          });
        });
    });
  };

  render() {
    const { editing } = this.state;
    const { editable, dataIndex, title, record, index, handleSave, ...restProps } = this.props;
    return (
      <td ref={node => (this.cell = node)} {...restProps}>
        {editable ? (
          <EditableContext.Consumer>
            {form => {
              this.form = form;
              return editing ? (
                <FormItem style={{ margin: 0 }}>
                  {form.getFieldDecorator(dataIndex, {
                    rules: [
                      {
                        required: true,
                        message: `${title} is required.`,
                      },
                    ],
                    initialValue: record[dataIndex],
                  })(<Input ref={node => (this.input = node)} onPressEnter={this.save} />)}
                </FormItem>
              ) : (
                <div
                  className="editable-cell-value-wrap"
                  style={{ paddingRight: 24 }}
                  onClick={this.toggleEdit}
                >
                  {restProps.children}
                </div>
              );
            }}
          </EditableContext.Consumer>
        ) : (
          restProps.children
        )}
      </td>
    );
  }
}

class SettingsContainer extends Component {
  state = {
    pagination: { current: 1, pageSize: 10, total: 0 },
    tableState: {
      sortField: null,
      sortOrder: null,
      searchTerm: null,
    },
  };

  columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      width: 200,
      sortOrder: 'ascend',
    },
    {
      title: 'Value',
      dataIndex: 'value',
      width: 150,
      editable: true,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      editable: true,
    },
    {
      title: 'Modify Date',
      dataIndex: 'update_dtime',
    },
    {
      title: 'Min SDK Version',
      dataIndex: 'm2m_relsno',
      width: 80,
    },
    {
      title: 'OS',
      width: 50,
      dataIndex: 'os',
      render: text => text || '-',
    },
  ];

  componentDidMount() {
    this.fetchTableData();
  }

  handleSearchTable(q) {
    // Clear sorting, pagination and search.
    this.setState(
      {
        pagination: { current: 1, pageSize: 10, total: 0 },
        tableState: {
          sortField: null,
          sortOrder: null,
          searchTerm: q,
        },
      },
      () => {
        this.fetchTableData();
      }
    );
  }

  handleTableChange = (pagination, filters, sorter) => {
    const pager = {
      ...this.state.pagination,
      current: pagination.current,
      total: this.props.settingsCount,
    };

    // Update state and fetch table data, pagination and sorter.  Filters are handled by search.
    const updateTableState = this.state.tableState;

    updateTableState.filteredInfo = filters;

    if (sorter) {
      updateTableState.sortField = sorter.columnKey;
      updateTableState.sortOrder = sorter.order;
    }

    this.setState({ pagination: pager, tableState: updateTableState }, () => {
      this.fetchTableData();
    });
  };

  fetchTableData() {
    this.props
      .getSettings({
        ...this.state.pagination,
        ...this.state.tableState,
      })
      .then(() =>
        this.setState({
          pagination: {
            ...this.state.pagination,
            total: this.props.settingsCount,
          },
        })
      )
      .catch(e => {
        notification.error({
          message: 'Error',
          description: typeof e === 'string' ? e : 'Could not retrieve settings.',
          duration: 0,
        });
      });
  }

  render() {
    const components = {
      body: {
        row: EditableFormRow,
        cell: EditableCell,
      },
    };

    const columns = this.columns.map(col => {
      if (!col.editable) {
        return col;
      }
      return {
        ...col,
        onCell: record => ({
          record,
          editable: col.editable,
          dataIndex: col.dataIndex,
          title: col.title,
          handleSave: this.props.handleSave,
        }),
      };
    });

    return (
      <Settings
        handleSearchTable={searchTerm => this.handleSearchTable(searchTerm)}
        components={components}
        pagination={this.state.pagination}
        handleTableChange={this.handleTableChange}
        tableData={this.props.settings}
        columns={columns}
        loading={this.props.loading}
      />
    );
  }
}

const mapStateToProps = state => ({
  loading:
    pending(state, 'settingsRead') ||
    pending(state, 'settingsUpdate') ||
    pending(state, 'settingsDelete'),
  rejected: rejected(state, 'settingsRead'),
  settings: fromEntities.getList(state, 'settings', fromResource.getList(state, 'settings')),
  settingsCount: get(state, 'resource.settings.count', 0),
});

const mapDispatchToProps = dispatch => ({
  handleSave: record => dispatch(resourceUpdateRequest('settings/:id', { id: record.id }, record)),
  getSettings: ({ searchTerm, current, pageSize }) =>
    dispatch(
      resourceListReadRequest('settings', {
        q: searchTerm,
        sort_by: 'name',
        page_num: current,
        page_size: pageSize,
      })
    ),
  deleteSetting: id => dispatch(resourceDeleteRequest(`settings/:id`, { id })),
});

export default compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(SettingsContainer);
